import React, { useMemo, useState } from 'react';
import { Box, useTheme, useMediaQuery, Drawer } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '@components/common/Sidebar';
import { useAuth } from '@services/authentication/auth.context';
import type { HeaderItem } from '@components/common/Header/interface';
import { DASHBOARD_PATH } from '@routes/path';
import { HEADER_HEIGHT } from '@services/constants/header';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './translations.json';

type SupportedLanguage = keyof typeof translations;

const SidebarLayout: React.FC = () => {
    const { user } = useAuth();
    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const settingItems: HeaderItem[] = useMemo(() => [
        {
            label: content.dashboard,
            path: DASHBOARD_PATH,
            icon: faGaugeHigh
        }
    ], [content.dashboard, user?.role]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    return (
        <Box 
            sx={{ 
                display: 'flex', 
                minHeight: '100vh', 
                flexDirection: { xs: 'column', md: 'row' },
                // Ajoute la marge en haut pour ne pas être caché sous le Header global
                marginTop: HEADER_HEIGHT 
            }}
        >

            {/* SIDEBAR RESPONSIVE */}
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: 240,
                            bgcolor: 'background.paper',
                            // Décale aussi l'intérieur du Drawer si tu veux qu'il commence sous le Header
                            marginTop: HEADER_HEIGHT, 
                            height: `calc(100% - 56px)`
                        },
                    }}
                >
                    <Sidebar
                        items={settingItems}
                        collapsed={false}
                        onToggleCollapse={handleDrawerToggle}
                        topOffset={0}
                    />
                </Drawer>
            ) : (
                <Sidebar
                    items={settingItems}
                    collapsed={collapsed}
                    onToggleCollapse={() => setCollapsed((prev) => !prev)}
                    topOffset={64}
                />
            )}

            {/* CONTENU PRINCIPAL */}
            <Box
                component="main"
                sx={{
                    flex: 1,
                    p: { xs: 2, sm: 3 },
                    overflowY: 'auto',
                    width: '100%',
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

export default SidebarLayout;